import { sensivelPorNome } from '../../nucleo/sensivel-por-nome';

/** Fase 2 (W32): verde é a paleta Malgo e o padrão; os outros seguem como opção. */
export const ACENTOS = ['verde', 'rosa', 'roxo', 'azul', 'ciano', 'laranja'] as const;
export type Acento = (typeof ACENTOS)[number];

export interface Aparencia {
  acento: Acento;
  tema_padrao: 'escuro' | 'claro';
  idioma_padrao: 'pt' | 'en' | 'es';
  /** Fase 2 (W4): fonte do texto do workspace (prévia ou `arquivo:<sha256>`), fonte pessoal liberada e marca. */
  fonte?: string;
  permitir_fonte_pessoal?: boolean;
  /** N5 (28/10): estilo de interface do workspace e a escolha pessoal liberada. */
  estilo_padrao?: string;
  permitir_estilo_pessoal?: boolean;
  marca_nome?: string | null;
  marca_logo?: string | null;
}

/** Fonte ou logo enviada em Configurações > Aparência (o conteúdo vem da rota pelo hash). */
export interface ArquivoAparencia {
  id: string;
  tipo: 'fonte' | 'logo';
  sha256: string;
  formato: 'woff2' | 'woff' | 'ttf' | 'otf' | 'png' | 'webp';
  tamanho: number;
  nome: string;
  created_at: string;
  familia?: string;
}

export interface Endereco {
  cep?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
}

export interface Empresa {
  id: string;
  razao_social: string;
  nome_fantasia: string | null;
  documento: string | null;
  /** Lote 19/10: país (ISO, padrão BR), tipo do documento (nulo no BR antigo = CNPJ) e moeda padrão (BRL, USD, EUR). */
  pais?: string;
  documento_tipo?: string | null;
  moeda?: string;
  endereco: Endereco;
  logo_anexo_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Sequencia {
  id: string;
  codigo: string;
  empresa_id: string | null;
  empresa_nome: string | null;
  prefixo: string;
  digitos: number;
  proximo: number;
  proximo_efetivo: number;
  reinicio_anual: boolean;
  ano_corrente: number | null;
  proxima_numeracao: string;
}

/** Fase 2 (W5, L10): telefone, documento, e-mail, CEP, dinheiro (centavos) e texto longo. */
export type TipoCampo = 'texto' | 'numero' | 'data' | 'lista' | 'booleano' | 'telefone' | 'documento' | 'email' | 'cep' | 'dinheiro' | 'texto_longo';

/** Tipos sempre sensíveis no modo discreto (a marca fica ligada e desabilitada na tela), a mesma lista do servidor. */
export const TIPOS_SEMPRE_SENSIVEIS: readonly TipoCampo[] = ['numero', 'data', 'telefone', 'documento', 'email', 'cep', 'dinheiro'];

export interface Campo {
  id: string;
  modulo: string;
  entidade: string;
  chave: string;
  rotulo: string;
  tipo: TipoCampo;
  opcoes: string[];
  obrigatorio: boolean;
  ordem: number;
  /** Dado sensível: oculto no modo discreto (review r15 spec M1). Número e data sempre; sim ou não, nunca. */
  sensivel?: boolean;
  /** Fase 2 (W5): texto de ajuda e condição de exibição ("mostrar quando"). */
  ajuda?: string | null;
  visivel_quando?: { campo: string; valor: string } | null;
}

export interface Visao {
  id: string;
  tela: string;
  nome: string;
  /** Review r27 segurança (M1): a lista de Configurações só traz quantas colunas a visão guarda. */
  qtd_colunas: number;
  padrao: boolean;
  /** E1 (R37-SPEC-M2): com quem a visão está compartilhada e se aparece em Relatórios. */
  compartilhamento?: 'privado' | 'papeis' | 'pessoas' | 'workspace';
  em_relatorios?: boolean;
  created_at: string;
}

export interface Modelo {
  id: string;
  codigo: string;
  nome: string;
  tipo: 'mensagem' | 'documento';
  canal: 'email' | 'whatsapp';
  assunto: string;
  corpo: string;
  ativo: boolean;
}

export interface EntradaAuditoria {
  id: string;
  acao: string;
  entidade: string;
  entidade_id: string | null;
  antes: unknown;
  depois: unknown;
  created_at: string;
  ator_tipo: 'usuario' | 'chave_api' | 'sistema';
  /** Nome do usuário ou, quando a ação veio por chave de API, o nome da chave. */
  autor_nome: string | null;
  /** Prefixo da chave de API (só quando ator_tipo é chave_api). */
  autor_prefixo: string | null;
  /** Visão salva: nomes atuais dos papéis e das pessoas com quem ela foi compartilhada (o antes e o depois guardam os ids). */
  nomes?: NomesReferencias;
}

export interface NomesReferencias {
  papeis: Record<string, { nome: string; codigo: string | null; de_fabrica: boolean }>;
  pessoas: Record<string, string>;
}

/** Papel de fábrica traduzido pelo código; personalizado, o nome gravado (o mesmo do Admin). */
export function nomeDoPapel(t: (chave: string, opcoes?: { defaultValue?: string }) => string, p: { nome: string; codigo?: string | null; de_fabrica?: boolean }): string {
  if (p.de_fabrica === false) return p.nome;
  const codigo = p.codigo ?? p.nome.trim().toLowerCase();
  return t(`papelFabrica.${codigo}`, { defaultValue: p.nome });
}

export interface EntradaHistoricoAparencia {
  id: string;
  valor_anterior: unknown;
  valor_novo: unknown;
  created_at: string;
  autor_nome: string | null;
}

/** Documento só com dígitos para CNPJ formatado. */
export function cnpjFormatado(d: string | null) {
  if (!d) return '';
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : d;
}

/** Mesma conta da função de numeração do núcleo: `{ano}` no prefixo e zeros à esquerda. */
export function formatarNumero(prefixo: string, digitos: number, numero: number, ano: number) {
  return prefixo.split('{ano}').join(String(ano)) + String(numero).padStart(digitos, '0');
}

/**
 * Marca "sensível" do campo (review r15 spec M1): número e data sempre (o modo discreto esconde pelo tipo); sim ou não, nunca;
 * texto e lista, o que o administrador escolheu. Num campo novo, enquanto a caixa não foi tocada, ela acompanha a regra por
 * nome: já vem marcada quando a chave ou o rótulo é sensível (`sensivelPorNome`: salário, orçamento, CPF, telefone...).
 */
export function sensivelDoFormulario(v: { tipo?: unknown; chave?: unknown; rotulo?: unknown; sensivel?: unknown }, automatico: boolean): boolean {
  if (TIPOS_SEMPRE_SENSIVEIS.includes(v.tipo as TipoCampo)) return true;
  if (v.tipo === 'booleano') return false;
  if (!automatico) return !!v.sensivel;
  return !!(sensivelPorNome(String(v.chave ?? '')) || sensivelPorNome(String(v.rotulo ?? '')));
}
